import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getCompetitionState } from '../features/leaderboard/leaderboardApi';
import './CompetitionStatusBadge.css';

const POLL_INTERVAL_MS = 5000;

interface CompetitionStatusBadgeProps {
  courseId: string;
}

/**
 * Student-facing indicator of whether competition mode is active for a course (US7/US1, FR-043,
 * FR-003): any enrolled student may now open the live leaderboard directly from here, since the
 * instructor-only restriction from FR-048 of `specs/001-shell-learning-platform/spec.md` was
 * superseded by this feature's FR-003.
 */
function CompetitionStatusBadge({ courseId }: CompetitionStatusBadgeProps) {
  const { t } = useTranslation();

  const stateQuery = useQuery({
    queryKey: ['student-competition-state', courseId],
    queryFn: () => getCompetitionState(courseId),
    enabled: Boolean(courseId),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const state = stateQuery.data;
  if (!state) {
    return null;
  }

  const isActive = state.active;
  const isFrozen = !isActive && Boolean(state.endedAt);

  if (!isActive && !isFrozen) {
    return null;
  }

  return (
    <div
      className={`competition-status-badge${isActive ? ' competition-status-badge--active' : ''}`}
      role="status"
    >
      <span className="competition-status-badge__label">
        {isActive ? t('leaderboard.statusActive') : t('leaderboard.statusFrozen')}
      </span>
      {isActive && (
        <Link className="competition-status-badge__cta" to={`/courses/${courseId}/leaderboard`}>
          {t('competitionBadge.viewLeaderboardCta')}
        </Link>
      )}
    </div>
  );
}

export default CompetitionStatusBadge;
